import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PostCard from "@/components/PostCard";
import CalmBadge from "@/components/CalmBadge";
import { Camera, Save, Lock, Globe, Eye, EyeOff, UserPlus, UserCheck, ShieldOff, BriefcaseBusiness, ExternalLink, Handshake } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const targetId = userId || user?.id;
  const isMe = !userId || userId === user?.id;

  if (!user?.id) return null;
  return <ProfileView key={JSON.stringify([user.id, targetId])} {...{ targetId, isMe, user, refresh, navigate }} />;
}

function ProfileView({ targetId, isMe, user, refresh, navigate }) {

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [creator, setCreator] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [blockBusy, setBlockBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const actionPending = useRef(false);
  const fileRef = useRef(null);
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; loadVersion.current += 1; };
  }, []);

  const load = useCallback(async () => {
    if (!targetId) return;
    const version = ++loadVersion.current;
    const current = () => mounted.current && version === loadVersion.current;
    setLoadError(false);
    try {
      const u = await api.get(`/users/${targetId}`, { timeout: 30000 });
      if (!current()) return;
      setProfile(u.data);

      setForm({
        display_name: u.data.display_name,
        username: u.data.username,
        bio: u.data.bio || "",
        privacy_mode: u.data.privacy_mode || "open",
        hide_like_counts: !!u.data.hide_like_counts,
        avatar_path: u.data.avatar_path,
        private_to_mutuals: !!u.data.private_to_mutuals,
        dm_only_from_mutuals: !!u.data.dm_only_from_mutuals,
      });

      const p = await api.get(`/posts/user/${targetId}`, { timeout: 30000 });
      if (!current()) return;
      setPosts(p.data);

      try {
        const c = await api.get(isMe ? "/creator/me" : `/creator/${targetId}`, { timeout: 30000 });
        if (!current()) return;
        if (isMe) {
          setCreator(c.data?.profile?.enabled ? { profile: c.data.profile, offerings: c.data.offerings || [] } : null);
        } else {
          setCreator(c.data);
        }
      } catch {
        if (current()) setCreator(null);
      }
    } catch {
      if (current()) setLoadError(true);
    }
  }, [targetId, isMe]);

  useEffect(() => {
    load();
  }, [load]);

  const beginAction = () => {
    if (!mounted.current || actionPending.current) return false;
    actionPending.current = true;
    setActionBusy(true);
    return true;
  };
  const finishAction = () => {
    actionPending.current = false;
    if (mounted.current) setActionBusy(false);
  };

  const save = async () => {
    if (!beginAction()) return;
    try {
      await api.patch("/users/me", form, { timeout: 30000 });
      if (!mounted.current) return;
      toast.success("Saved");
      setEditing(false);
      await refresh();
      if (mounted.current) await load();
    } catch (e) {
      if (mounted.current) toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      finishAction();
    }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !beginAction()) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
      if (!mounted.current) return;
      setForm((current) => ({ ...current, avatar_path: r.data.path }));
      toast.success("Avatar uploaded — hit Save");
    } catch {
      if (mounted.current) toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      finishAction();
    }
  };

  const toggleFollow = async () => {
    if (!beginAction()) return;
    try {
      const r = await api.post(`/users/${targetId}/follow`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      setProfile((current) => ({ ...current, i_follow: r.data.following }));
    } catch (e) {
      if (mounted.current) toast.error(e.response?.data?.detail || "Could not update follow status");
    } finally {
      finishAction();
    }
  };

  const toggleBlock = async () => {
    if (!targetId || isMe || actionPending.current) return;
    const alreadyBlocked = !!profile?.i_blocked;
    if (!alreadyBlocked && !window.confirm(`Block @${profile?.username}? They will no longer be able to interact with you.`)) return;
    if (!beginAction()) return;

    setBlockBusy(true);
    try {
      const r = await api.post(`/users/${targetId}/block`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      const blocked = typeof r.data?.blocked === "boolean" ? r.data.blocked : !alreadyBlocked;
      setProfile((p) => ({ ...p, i_blocked: blocked, i_follow: blocked ? false : p.i_follow }));
      toast.success(blocked ? "User blocked" : "User unblocked");
      if (blocked) navigate("/feed");
    } catch (e) {
      if (mounted.current) toast.error(e.response?.data?.detail || "Could not update block status");
    } finally {
      if (mounted.current) setBlockBusy(false);
      finishAction();
    }
  };

  if (loadError) return <div role="alert" className="brutal-card bg-card p-5">Could not load profile. <button onClick={load} className="brutal-btn px-3 py-2">Retry</button></div>;
  if (!profile) return <div className="brutal-card bg-card p-5">Loading…</div>;

  return (
    <div data-testid="profile-page">
      <fieldset disabled={actionBusy} aria-busy={actionBusy} aria-label="Profile controls" className="brutal-card bg-sage p-6 mb-6 min-w-0">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl border-2 border-foreground overflow-hidden bg-accent flex items-center justify-center font-display text-4xl">
              {(editing ? form.avatar_path : profile.avatar_path) ? <img src={fileUrl(editing ? form.avatar_path : profile.avatar_path)} alt="" className="w-full h-full object-cover" /> : profile.display_name?.[0]}
            </div>
            {editing && isMe && (
              <>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={uploadAvatar} data-testid="avatar-file-input" />
                <button onClick={() => fileRef.current?.click()} className="absolute -bottom-2 -right-2 brutal-btn bg-card p-2" data-testid="change-avatar-btn">
                  <Camera size={14} strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {!editing ? (
              <>
                <h1 className="text-4xl flex items-center gap-3 flex-wrap">
                  <span>{profile.display_name}</span>
                  {profile.is_premium && <CalmBadge />}
                </h1>
                <div className="text-sm text-muted-foreground">@{profile.username}</div>
                <p className="mt-2 max-w-2xl">{profile.bio || (isMe ? "Add a bio in edit mode." : "")}</p>
                <div className="mt-3 flex gap-3 text-sm">
                  <span><b>{posts.length}</b> posts</span>
                  <span><b>{profile.followers_count}</b> followers</span>
                  {!profile.hide_like_counts && <span><b>{profile.following_count}</b> following</span>}
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <input className="brutal-input w-full px-3 py-2" required maxLength={80} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Display name" data-testid="edit-name" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-70 ml-1">Username</div>
                  <div className="flex items-center brutal-input mt-1">
                    <span aria-hidden="true" className="px-2 text-muted-foreground select-none">@</span>
                    <input aria-label="Username" className="flex-1 px-1 py-2 text-sm bg-transparent outline-none" required minLength={3} maxLength={30} pattern="[a-z0-9][a-z0-9_]{2,29}" title="3–30 lowercase letters, numbers, or underscores" value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="username" data-testid="edit-username" />
                  </div>
                </div>
                <textarea rows={2} maxLength={500} placeholder="Bio" className="brutal-input w-full px-3 py-2" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="edit-bio" />
              </div>
            )}
          </div>
          {isMe && (
            <div className="flex gap-2">
              {editing ? (
                <>
                  <button onClick={() => setEditing(false)} className="brutal-btn bg-card px-3 py-2">Cancel</button>
                  <button onClick={save} className="brutal-btn bg-primary text-primary-foreground px-3 py-2 flex items-center gap-1" data-testid="save-profile-btn"><Save size={14} strokeWidth={2.5} /> Save</button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} className="brutal-btn bg-card px-3 py-2" data-testid="edit-profile-btn">Edit profile</button>
              )}
            </div>
          )}
          {!isMe && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={toggleFollow} disabled={profile.i_blocked} data-testid="follow-btn"
                className={`brutal-btn px-4 py-2 flex items-center gap-2 disabled:opacity-50 ${profile.i_follow ? "bg-foreground text-background" : "bg-primary text-primary-foreground"}`}>
                {profile.i_follow ? <><UserCheck size={14} strokeWidth={2.5} /> Following</> : <><UserPlus size={14} strokeWidth={2.5} /> Follow</>}
              </button>
              <button
                onClick={toggleBlock}
                disabled={blockBusy}
                data-testid="block-user-btn"
                className={`brutal-btn px-4 py-2 flex items-center gap-2 disabled:opacity-50 ${profile.i_blocked ? "bg-destructive text-destructive-foreground" : "bg-card"}`}
              >
                <ShieldOff size={14} strokeWidth={2.5} />
                {blockBusy ? "Updating…" : profile.i_blocked ? "Unblock" : "Block user"}
              </button>
            </div>
          )}
        </div>

        {editing && isMe && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="brutal-card bg-card p-3">
              <div className="text-xs uppercase tracking-wider font-semibold mb-2">Privacy mode</div>
              <div className="flex gap-2">
                {[{ k: "open", L: Globe, l: "Open" }, { k: "friends", L: Eye, l: "Friends" }, { k: "private", L: Lock, l: "Private" }].map(({ k, L, l }) => (
                  <button key={k} onClick={() => setForm({ ...form, privacy_mode: k })} data-testid={`priv-${k}`}
                    className={`brutal-btn px-3 py-2 text-sm flex items-center gap-1 ${form.privacy_mode === k ? "bg-foreground text-background" : "bg-card"}`}>
                    <L size={14} strokeWidth={2.5} /> {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="brutal-card bg-card p-3 flex items-center gap-3">
              <button onClick={() => setForm({ ...form, hide_like_counts: !form.hide_like_counts })} data-testid="toggle-hide-likes"
                className={`brutal-btn px-3 py-2 text-sm flex items-center gap-1 ${form.hide_like_counts ? "bg-foreground text-background" : "bg-card"}`}>
                <EyeOff size={14} strokeWidth={2.5} /> {form.hide_like_counts ? "Hidden" : "Visible"}
              </button>
              <div className="text-xs">Hide like counts on my feed</div>
            </div>
            <div className="brutal-card bg-accent p-3 sm:col-span-2" data-testid="calm-privacy-card">
              <div className="text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                <CalmBadge size="xs" /> Advanced privacy
              </div>
              {user?.premium?.active ? (
                <div className="grid sm:grid-cols-2 gap-2">
                  <button onClick={() => setForm({ ...form, private_to_mutuals: !form.private_to_mutuals })}
                    data-testid="toggle-private-mutuals"
                    className={`brutal-btn px-3 py-2 text-sm text-left ${form.private_to_mutuals ? "bg-foreground text-background" : "bg-card"}`}>
                    <div className="font-semibold">{form.private_to_mutuals ? "On" : "Off"} · Hide me from non-mutuals</div>
                    <div className="text-xs opacity-80 font-normal mt-0.5">Only mutual friends see my public posts.</div>
                  </button>
                  <button onClick={() => setForm({ ...form, dm_only_from_mutuals: !form.dm_only_from_mutuals })}
                    data-testid="toggle-dm-mutuals"
                    className={`brutal-btn px-3 py-2 text-sm text-left ${form.dm_only_from_mutuals ? "bg-foreground text-background" : "bg-card"}`}>
                    <div className="font-semibold">{form.dm_only_from_mutuals ? "On" : "Off"} · DMs from mutuals only</div>
                    <div className="text-xs opacity-80 font-normal mt-0.5">Block strangers from sliding into your inbox.</div>
                  </button>
                </div>
              ) : (
                <div className="text-xs flex items-center justify-between gap-3 flex-wrap">
                  <span>Hide from non-mutuals · stricter DM filter — included in HAEVEN Premium.</span>
                  <a href="/premium" className="brutal-btn bg-primary text-primary-foreground px-3 py-1 text-xs">See Premium</a>
                </div>
              )}
            </div>
          </div>
        )}
      </fieldset>

      {creator?.profile?.enabled && (
        <section className="brutal-card bg-card p-5 mb-6" data-testid="creator-public-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold"><BriefcaseBusiness size={15} /> {creator.profile.account_type}</div>
              <h2 className="text-2xl mt-2">{creator.profile.headline || "Creator on HAEVEN"}</h2>
              {creator.profile.about && <p className="mt-2 max-w-3xl text-sm">{creator.profile.about}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {(creator.profile.categories || []).map((category) => <span key={category} className="px-2 py-1 rounded-lg border border-foreground/20 text-xs">{category}</span>)}
              </div>
              {creator.profile.accepting_collaborations && <div className="mt-3 text-sm flex items-center gap-2"><Handshake size={15} /> Open to collaborations</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              {creator.profile.website && <a href={creator.profile.website} target="_blank" rel="noreferrer" className="brutal-btn bg-card px-3 py-2 text-sm inline-flex items-center gap-2">Website <ExternalLink size={14} /></a>}
              {isMe && <Link to="/creator-studio" className="brutal-btn bg-primary text-primary-foreground px-3 py-2 text-sm">Manage Creator Studio</Link>}
            </div>
          </div>

          {(creator.offerings || []).length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider font-bold mb-2">Offerings</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(creator.offerings || []).filter((item) => item.active !== false).slice(0, 6).map((item) => (
                  <div key={item.id} className="border-2 border-foreground rounded-xl p-3 bg-background">
                    <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{item.offering_type?.replaceAll("_", " ")}</div>
                    <div className="font-semibold mt-1">{item.title}</div>
                    {item.description && <p className="text-xs mt-1 opacity-80">{item.description}</p>}
                    {item.price_label && <div className="text-sm font-bold mt-2">{item.price_label}</div>}
                    <div className="text-[10px] mt-2 opacity-60">Catalog only · transaction availability depends on platform and offering type.</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <h2 className="text-2xl mb-3">Posts</h2>
      <div className="space-y-5">
        {posts.length === 0 ? <div className="brutal-card p-6 bg-card">No posts yet.</div> : posts.map((p) => <PostCard key={p.id} post={p} hideLikeCounts={profile.hide_like_counts} />)}
      </div>
    </div>
  );
}
